/**
 * Auth helpers – token storage and session from API.
 * Backend is the source of truth; frontend stores JWT for API calls.
 */

import { getCurrentUser, getToken, setToken, clearToken, type AuthUser } from "@/lib/api";

export type { AuthUser };

/** Get current user from API (server or client). */
export async function auth(): Promise<AuthUser | null> {
  return getCurrentUser();
}

/** Get business ID for tenant-scoped requests; null if no business. */
export async function getCurrentBusinessId(): Promise<string | null> {
  const user = await getCurrentUser();
  return user?.businessId ?? null;
}

/** Require auth and business; throw/redirect in layout if not. */
export async function requireBusinessUser(): Promise<AuthUser> {
  const user = await getCurrentUser();
  if (!user) throw new Error("UNAUTHORIZED");
  if (!user.businessId) throw new Error("NO_BUSINESS");
  return user;
}

/** Require platform admin. */
export async function requireAdmin(): Promise<AuthUser> {
  const user = await getCurrentUser();
  if (!user) throw new Error("UNAUTHORIZED");
  if (user.role !== "PLATFORM_ADMIN") throw new Error("FORBIDDEN");
  return user;
}

/** True if user is STAFF role (dashboard access with limited nav). */
export function isStaffRole(user: AuthUser | null): boolean {
  return user?.role === "STAFF";
}

/** True if user can access business Settings (owner only). */
export function canAccessSettings(user: AuthUser | null): boolean {
  return user?.role === "BUSINESS_OWNER" || user?.role === "PLATFORM_ADMIN";
}

/** True if user can manage staff list and invite/revoke (owner or manager). */
export function canManageStaff(user: AuthUser | null): boolean {
  return (
    user?.role === "BUSINESS_OWNER" ||
    user?.role === "MANAGER" ||
    user?.role === "PLATFORM_ADMIN"
  );
}

/** True if user can access Services, Locations, Customers, Reviews (owner or manager; not STAFF). */
export function canAccessFullNav(user: AuthUser | null): boolean {
  return (
    user?.role === "BUSINESS_OWNER" ||
    user?.role === "MANAGER" ||
    user?.role === "PLATFORM_ADMIN"
  );
}

export { getToken, setToken, clearToken };
